import { registerAacEncoder } from '@mediabunny/aac-encoder'
import {
  AudioBufferSource,
  BufferTarget,
  canEncodeAudio,
  EncodedPacket,
  EncodedVideoPacketSource,
  type IsobmffOutputFormatOptions,
  Mp4OutputFormat,
  Output,
  StreamTarget,
  type StreamTargetChunk,
  type Target,
} from 'mediabunny'
import {
  type GraphicsBackendKind,
  type GraphicsConfig,
  resolveGraphicsConfig,
} from '@/graphics/GraphicsConfig'
import { getLoggerManager } from '@/shared/logging'
import { closeAudioContext, type PreparedVideoAudio, prepareAudioBuffer } from './BrowserVideoAudio'
import type { BrowserVideoMp4OutputSink } from './BrowserVideoMp4Sink'
import { createBrowserVideoRenderRuntime } from './BrowserVideoRenderRuntime'
import { ManualFrameClock } from './ManualFrameClock'
import type {
  BrowserVideoRenderRuntime,
  FrameReadbackResult,
  ManualFrameRequest,
  VideoRenderConfig,
  VideoRenderDiagnosticEvent,
  VideoRenderDiagnostics,
} from './types'

const log = getLoggerManager().getLogger('videoRender')

// Android WebView versions in the field may expose AudioContext but not a
// native AAC WebCodecs encoder. Register Mediabunny's portable encoder only
// when the exact decoded audio format is not natively supported.
const aacEncoderRegistrationPromises = new Map<string, Promise<void>>()

async function ensureAacEncoder(audioBuffer: AudioBuffer): Promise<void> {
  const sampleRate = audioBuffer.sampleRate
  const numberOfChannels = audioBuffer.numberOfChannels
  const key = `${numberOfChannels.toString()}@${sampleRate.toString()}`
  const nativeSupported = await canEncodeAudio('aac', {
    numberOfChannels,
    sampleRate,
    bitrate: 128_000,
  })
  if (nativeSupported) {
    log.info(`[videoRender] native AAC encoder available (${numberOfChannels}ch/${sampleRate}Hz)`)
    return
  }
  let registration = aacEncoderRegistrationPromises.get(key)
  if (!registration) {
    registration = Promise.resolve().then(() => {
      registerAacEncoder()
      log.info(
        `[videoRender] registered portable AAC encoder (${numberOfChannels}ch/${sampleRate}Hz)`
      )
    })
    aacEncoderRegistrationPromises.set(key, registration)
  }
  await registration
}

export type BrowserVideoMp4ExportStage =
  | 'runtime'
  | 'stabilize'
  | 'template'
  | 'render'
  | 'encode'
  | 'complete'
  | 'cancelled'
  | 'error'

export interface BrowserVideoMp4ExportSettings {
  width: number
  height: number
  fps: number
  durationSeconds: number
  pixelRatio?: number
  quality?: number
}

export interface BrowserVideoMp4ExportProgress {
  stage: BrowserVideoMp4ExportStage
  frameNumber: number
  totalFrames: number
  progress: number
  message: string
  /** Optional diagnostic checkpoint for the export tail. */
  phase?: BrowserVideoMp4ExportPhase
  /** Elapsed time for the reported checkpoint, in milliseconds. */
  elapsedMs?: number
  /** Output size when the encoded buffer becomes available. */
  byteLength?: number
  /** Lifecycle status for a diagnostic checkpoint. */
  phaseStatus?: BrowserVideoMp4ExportPhaseEvent['status']
  diagnostics?: VideoRenderDiagnostics
}

export const BROWSER_VIDEO_MP4_EXPORT_PHASES = [
  'runtime.init',
  'stabilize',
  'template',
  'frame-render',
  'frame-readback',
  'frame-encode',
  'encoder.flush',
  'packet-writes',
  'audio.prepare',
  'audio-pump',
  'audio-pump.wait',
  'audio.aac-prepare',
  'audio.encode',
  'mp4.finalize',
  'blob.array-buffer',
  'device-lost',
  'export.total',
] as const

export type BrowserVideoMp4ExportPhase = (typeof BROWSER_VIDEO_MP4_EXPORT_PHASES)[number]

export interface BrowserVideoMp4ExportPhaseEvent {
  phase: BrowserVideoMp4ExportPhase
  status: 'start' | 'complete' | 'failed'
  elapsedMs: number
  frameNumber?: number
  requestedBackend?: GraphicsBackendKind
  actualBackend?: GraphicsBackendKind
}

export interface BrowserVideoMp4ExportResult {
  blob: Blob
  arrayBuffer: ArrayBuffer
  mimeType: 'video/mp4'
  extension: 'mp4'
  width: number
  height: number
  fps: number
  durationSeconds: number
  framesEncoded: number
  byteLength: number
  diagnosticEvents: VideoRenderDiagnosticEvent[]
}

export interface BrowserVideoMp4FrameEncoder {
  readonly mimeType: 'video/mp4'
  readonly extension: 'mp4'
  /** Audio is optional; preparation failures fall back to a video-only export. */
  start(
    settings: Required<BrowserVideoMp4ExportSettings>,
    options?: {
      audioUrl?: string
      signal?: AbortSignal
      diagnosticId?: string
      onPhase?: (event: BrowserVideoMp4ExportPhaseEvent) => void
    }
  ): Promise<void>
  addFrame(
    frame: FrameReadbackResult,
    request: ManualFrameRequest,
    signal?: AbortSignal
  ): Promise<void>
  finish(signal?: AbortSignal): Promise<Blob>
  cancel(): Promise<void>
  dispose(): Promise<void>
}

export interface BrowserVideoMp4StreamEncoderFinishResult {
  byteLength: number
}

export interface BrowserVideoMp4StreamFrameEncoder {
  readonly mimeType: 'video/mp4'
  readonly extension: 'mp4'
  start(
    settings: Required<BrowserVideoMp4ExportSettings>,
    options?: {
      audioUrl?: string
      signal?: AbortSignal
      diagnosticId?: string
      onPhase?: (event: BrowserVideoMp4ExportPhaseEvent) => void
    }
  ): Promise<void>
  addFrame(
    frame: FrameReadbackResult,
    request: ManualFrameRequest,
    signal?: AbortSignal
  ): Promise<void>
  finish(signal?: AbortSignal): Promise<BrowserVideoMp4StreamEncoderFinishResult>
  cancel(): Promise<void>
  dispose(): Promise<void>
}

export interface BrowserVideoMp4StreamExportResult {
  mimeType: 'video/mp4'
  extension: 'mp4'
  width: number
  height: number
  fps: number
  durationSeconds: number
  framesEncoded: number
  byteLength: number
  fragmented: true
  diagnosticEvents: VideoRenderDiagnosticEvent[]
}

export type BrowserVideoMp4StreamExportSettings = BrowserVideoMp4ExportSettings
export type BrowserVideoMp4StreamExportProgress = BrowserVideoMp4ExportProgress

export interface BrowserVideoMp4ExportOptions {
  container: HTMLElement
  settings: BrowserVideoMp4ExportSettings
  scene?: unknown
  timelineTemplate?: unknown
  runtimeConfig?: Partial<VideoRenderConfig>
  runtime?: BrowserVideoRenderRuntime
  encoder?: BrowserVideoMp4FrameEncoder
  audioUrl?: string
  /** Host-provided correlation id used only in diagnostic logs. */
  diagnosticId?: string
  /** Optional callback for non-frame export checkpoints. */
  onPhase?: (event: BrowserVideoMp4ExportPhaseEvent) => void
  signal?: AbortSignal
  onProgress?: (progress: BrowserVideoMp4ExportProgress) => void
}

export interface BrowserVideoMp4StreamExportOptions
  extends Omit<BrowserVideoMp4ExportOptions, 'settings' | 'encoder'> {
  settings: BrowserVideoMp4StreamExportSettings
  sink: BrowserVideoMp4OutputSink
  encoder?: BrowserVideoMp4StreamFrameEncoder
  maxDurationSeconds?: number
  maxOutputFrames?: number
  chunkSize?: number
}

interface BrowserVideoMp4FrameEncoderCore<Result> {
  readonly mimeType: 'video/mp4'
  readonly extension: 'mp4'
  start(
    settings: Required<BrowserVideoMp4ExportSettings>,
    options?: {
      audioUrl?: string
      signal?: AbortSignal
      diagnosticId?: string
      onPhase?: (event: BrowserVideoMp4ExportPhaseEvent) => void
    }
  ): Promise<void>
  addFrame(
    frame: FrameReadbackResult,
    request: ManualFrameRequest,
    signal?: AbortSignal
  ): Promise<void>
  finish(signal?: AbortSignal): Promise<Result>
  cancel(): Promise<void>
  dispose(): Promise<void>
}

interface BrowserVideoMp4EncoderOutput<Result> {
  target: Target
  formatOptions: IsobmffOutputFormatOptions
  finalize(): Promise<Result> | Result
  abort(reason?: unknown): Promise<void>
}

export interface BrowserVideoMp4StreamExportLimits {
  maxDurationSeconds?: number
  maxOutputFrames?: number
}

export const DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_DURATION_SECONDS = 3600
export const DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_OUTPUT_FRAMES = 36_000

export function normalizeBrowserVideoMp4ExportSettings(
  input: Partial<BrowserVideoMp4ExportSettings> = {}
): Required<BrowserVideoMp4ExportSettings> {
  return {
    width: clampEvenInteger(input.width, 64, 3840, 1280),
    height: clampEvenInteger(input.height, 64, 2160, 720),
    fps: clampInteger(input.fps, 1, 60, 24),
    durationSeconds: clampNumber(input.durationSeconds, 0.1, 60, 4),
    pixelRatio: clampNumber(input.pixelRatio, 0.25, 4, 1),
    quality: clampNumber(input.quality, 0.1, 1, 0.82),
  }
}

export function normalizeBrowserVideoMp4StreamExportSettings(
  input: Partial<BrowserVideoMp4ExportSettings> = {},
  limits: BrowserVideoMp4StreamExportLimits = {}
): Required<BrowserVideoMp4ExportSettings> {
  const settings = {
    width: clampEvenInteger(input.width, 64, 3840, 1280),
    height: clampEvenInteger(input.height, 64, 2160, 720),
    fps: clampInteger(input.fps, 1, 60, 24),
    durationSeconds: 0,
    pixelRatio: clampNumber(input.pixelRatio, 0.25, 4, 1),
    quality: clampNumber(input.quality, 0.1, 1, 0.82),
  }
  const maxDurationSeconds = positiveFiniteNumber(
    limits.maxDurationSeconds,
    DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_DURATION_SECONDS
  )
  const maxOutputFrames = positiveIntegerNumber(
    limits.maxOutputFrames,
    DEFAULT_BROWSER_VIDEO_MP4_STREAM_MAX_OUTPUT_FRAMES
  )
  const durationSeconds = input.durationSeconds
  if (
    typeof durationSeconds !== 'number' ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds < 0.1
  ) {
    throw new Error('Browser MP4 stream export requires durationSeconds >= 0.1.')
  }
  if (durationSeconds > maxDurationSeconds) {
    throw new Error(
      `Browser MP4 stream export duration ${durationSeconds}s exceeds the configured limit of ${maxDurationSeconds}s.`
    )
  }
  const totalFrames = Math.max(1, Math.ceil(durationSeconds * settings.fps))
  if (totalFrames > maxOutputFrames) {
    throw new Error(
      `Browser MP4 stream export frame count ${totalFrames} exceeds the configured limit of ${maxOutputFrames}.`
    )
  }
  settings.durationSeconds = durationSeconds
  return settings
}

function createBrowserVideoMp4FrameEncoderCore<Result>(
  settings: Required<BrowserVideoMp4ExportSettings>,
  encoderOutput: BrowserVideoMp4EncoderOutput<Result>
): BrowserVideoMp4FrameEncoderCore<Result> {
  if (typeof VideoEncoder === 'undefined') {
    throw new Error(createMissingVideoEncoderMessage())
  }

  let output: Output<Mp4OutputFormat, Target> | null = null
  let source: EncodedVideoPacketSource | null = null
  let encoder: VideoEncoder | null = null
  let encoderError: Error | null = null
  const pendingPacketWrites = new Set<Promise<void>>()
  let cleanupPromise: Promise<void> | null = null
  let stopped = false
  let finalized = false
  let width = settings.width
  let height = settings.height
  let fps = settings.fps
  let bitrate = estimateVideoBitsPerSecond(settings)
  let codec = createH264CodecString(height)
  let audioSource: AudioBufferSource | null = null
  let audioContext: AudioContext | null = null
  let audioPump: Promise<void> | null = null
  let audioPumpError: unknown | null = null
  let audioPreparation: Promise<PreparedVideoAudio | null> | null = null
  let audioPreparationAbortController: AbortController | null = null
  let audioPreparationAbortCleanup: (() => void) | null = null
  let diagnosticId = ''
  let phaseReporter: ((event: BrowserVideoMp4ExportPhaseEvent) => void) | undefined

  const createEncoder = () => {
    if (!source) {
      throw new Error('Video encoder source has not been started.')
    }
    encoderError = null
    const nextEncoder = new VideoEncoder({
      output: (chunk, meta) => {
        const encodedPacket = EncodedPacket.fromEncodedChunk(chunk)
        const packetWrite = source?.add(encodedPacket, meta) ?? Promise.resolve()
        pendingPacketWrites.add(packetWrite)
        packetWrite
          .catch((error: Error) => {
            encoderError = error
          })
          .finally(() => {
            pendingPacketWrites.delete(packetWrite)
          })
      },
      error: (error) => {
        encoderError = error
      },
    })
    nextEncoder.configure({ codec, width, height, bitrate })
    return nextEncoder
  }

  return {
    mimeType: 'video/mp4',
    extension: 'mp4',
    async start(nextSettings, options) {
      width = nextSettings.width
      height = nextSettings.height
      fps = Math.max(1, nextSettings.fps)
      bitrate = estimateVideoBitsPerSecond(nextSettings)
      codec = createH264CodecString(nextSettings.height)
      await cleanupPromise
      cleanupPromise = null
      stopped = false
      finalized = false
      pendingPacketWrites.clear()
      audioPumpError = null
      audioPreparation = null
      audioPreparationAbortController = null
      audioPreparationAbortCleanup = null
      diagnosticId = options?.diagnosticId ?? ''
      phaseReporter = options?.onPhase
      output = new Output({
        format: new Mp4OutputFormat(encoderOutput.formatOptions),
        target: encoderOutput.target,
      })
      source = new EncodedVideoPacketSource('avc')
      output.addVideoTrack(source, {
        rotation: 0,
        frameRate: fps,
      })
      if (options?.audioUrl) {
        try {
          // Mediabunny requires tracks before Output.start(). Register an empty
          // track now, while the fetch/decode runs in parallel with video work.
          audioSource = new AudioBufferSource({ codec: 'aac', bitrate: 128_000 })
          output.addAudioTrack(audioSource)
          const audioAbortController =
            typeof AbortController === 'function' ? new AbortController() : null
          audioPreparationAbortController = audioAbortController
          const audioSignal = audioAbortController?.signal ?? options.signal
          if (audioAbortController && options.signal) {
            const abortAudioPreparation = () => audioAbortController.abort()
            if (options.signal.aborted) {
              audioAbortController.abort()
            } else {
              options.signal.addEventListener('abort', abortAudioPreparation, { once: true })
              audioPreparationAbortCleanup = () => {
                options.signal?.removeEventListener('abort', abortAudioPreparation)
              }
            }
          }
          audioPreparation = prepareAudioBuffer(
            options.audioUrl,
            nextSettings.durationSeconds,
            audioSignal,
            diagnosticId,
            phaseReporter
          ).then(
            async (preparedAudio) => {
              if (stopped || audioAbortController?.signal.aborted) {
                await closeAudioContext(preparedAudio?.context ?? null)
                return null
              }
              return preparedAudio
            },
            (error: unknown) => {
              if (!stopped && !audioSignal?.aborted) {
                log.warn(
                  `[videoRender] audio unavailable; continuing without audio: ${getErrorMessage(error)}`
                )
              }
              return null
            }
          )
        } catch (error) {
          log.warn(
            `[videoRender] audio track unavailable; continuing without audio: ${getErrorMessage(error)}`
          )
          audioSource = null
          audioPreparation = null
        }
      }
      await output.start()
      encoder = createEncoder()
      if (audioPreparation && audioSource) {
        const pendingAudioSource = audioSource
        const pendingAudioAbortController = audioPreparationAbortController
        const closePendingAudioSource = () => {
          try {
            pendingAudioSource.close()
          } catch (error: unknown) {
            if (!stopped) {
              audioPumpError ??= error
              log.error(`[videoRender] audio source close failed: ${getErrorMessage(error)}`)
            }
          }
        }
        // Video frame work can proceed while audio is fetched and decoded;
        // finish() waits for this pump before finalizing the muxer.
        const audioPumpStartedAt = getLoggerManager().now()
        logVideoRenderPhase(
          'audio-pump',
          'start',
          diagnosticId,
          audioPumpStartedAt,
          '',
          phaseReporter
        )
        audioPump = audioPreparation
          .then(async (preparedAudio) => {
            if (!preparedAudio) {
              closePendingAudioSource()
              return
            }
            if (stopped || pendingAudioAbortController?.signal.aborted) {
              await closeAudioContext(preparedAudio.context)
              closePendingAudioSource()
              return
            }
            log.info(
              `[videoRender] decoded audio (${preparedAudio.buffer.numberOfChannels}ch/${preparedAudio.buffer.sampleRate}Hz, ${preparedAudio.buffer.duration.toFixed(3)}s)`
            )
            try {
              await runVideoRenderPhase(
                'audio.aac-prepare',
                diagnosticId,
                () => ensureAacEncoder(preparedAudio.buffer),
                phaseReporter
              )
            } catch (error: unknown) {
              if (!stopped && !pendingAudioAbortController?.signal.aborted) {
                log.warn(
                  `[videoRender] audio unavailable; continuing without audio: ${getErrorMessage(error)}`
                )
              }
              await closeAudioContext(preparedAudio.context)
              closePendingAudioSource()
              return
            }
            if (stopped || pendingAudioAbortController?.signal.aborted) {
              await closeAudioContext(preparedAudio.context)
              closePendingAudioSource()
              return
            }
            audioContext = preparedAudio.context
            try {
              await runVideoRenderPhase(
                'audio.encode',
                diagnosticId,
                () => pendingAudioSource.add(preparedAudio.buffer),
                phaseReporter
              )
              closePendingAudioSource()
            } catch (error: unknown) {
              if (stopped || pendingAudioAbortController?.signal.aborted) {
                await closeAudioContext(preparedAudio.context)
                return
              }
              audioPumpError = error
              log.error(`[videoRender] audio encode failed: ${getErrorMessage(error)}`)
            }
          })
          .catch((error: unknown) => {
            if (!stopped) {
              audioPumpError = error
              log.error(`[videoRender] audio encode failed: ${getErrorMessage(error)}`)
            }
          })
          .then(() => {
            logVideoRenderPhase(
              'audio-pump',
              audioPumpError == null ? 'complete' : 'failed',
              diagnosticId,
              audioPumpStartedAt,
              '',
              phaseReporter
            )
          })
      }
    },
    async addFrame(frame, request, signal) {
      assertNotStopped(stopped)
      if (!encoder) {
        throw new Error('Video encoder has not been started.')
      }
      throwIfAborted(signal)

      const videoFrame = new VideoFrame(frame.data, {
        format: 'RGBA',
        codedWidth: frame.width,
        codedHeight: frame.height,
        timestamp: Math.floor(request.timestampSeconds * 1_000_000),
        duration: Math.floor(1_000_000 / fps),
      })
      try {
        while (encoder.encodeQueueSize > 5) {
          throwIfAborted(signal)
          await sleep(1, signal)
        }

        let forceKeyFrame = request.frameNumber === 0
        if (encoder.state === 'closed' && encoderError?.message?.includes('reclaimed')) {
          encoder = createEncoder()
          forceKeyFrame = true
        }
        if (encoderError) {
          throw encoderError
        }

        encoder.encode(videoFrame, { keyFrame: forceKeyFrame })
      } finally {
        videoFrame.close()
      }
    },
    async finish(signal) {
      if (!output) {
        throw new Error('Video encoder has not been started.')
      }
      throwIfAborted(signal)
      if (encoder && encoder.state !== 'closed') {
        await runVideoRenderPhase(
          'encoder.flush',
          diagnosticId,
          () => withAbort(encoder!.flush(), signal),
          phaseReporter
        )
      }
      throwIfAborted(signal)
      await runVideoRenderPhase(
        'packet-writes',
        diagnosticId,
        () => withAbort(Promise.all(pendingPacketWrites), signal),
        phaseReporter
      )
      if (audioPump) {
        await runVideoRenderPhase(
          'audio-pump.wait',
          diagnosticId,
          () => withAbort(audioPump!, signal),
          phaseReporter
        )
      }
      if (audioPumpError) {
        throw audioPumpError
      }
      if (encoderError) {
        throw encoderError
      }
      throwIfAborted(signal)
      await runVideoRenderPhase(
        'mp4.finalize',
        diagnosticId,
        () => withAbort(output!.finalize(), signal),
        phaseReporter
      )
      stopped = true
      finalized = true
      return await encoderOutput.finalize()
    },
    async cancel() {
      const pendingAudioPump = audioPump
      const pendingAudioContext = audioContext
      const pendingAudioAbortController = audioPreparationAbortController
      const pendingAudioAbortCleanup = audioPreparationAbortCleanup
      stopped = true
      pendingAudioAbortController?.abort()
      pendingAudioAbortCleanup?.()
      if (encoder && encoder.state !== 'closed') {
        encoder.close()
      }
      cleanupPromise ??= output && !finalized ? output.cancel() : Promise.resolve()
      pendingPacketWrites.clear()
      encoder = null
      output = null
      source = null
      audioSource = null
      audioPump = null
      audioContext = null
      audioPreparation = null
      audioPreparationAbortController = null
      audioPreparationAbortCleanup = null
      try {
        await cleanupPromise
      } finally {
        if (!finalized) {
          await warnOnCleanupFailure(
            encoderOutput.abort(),
            'Video export encoder output abort failed.'
          )
        }
        await settleAudioPump(pendingAudioPump)
        await closeAudioContext(pendingAudioContext)
        audioPumpError = null
      }
    },
    async dispose() {
      const pendingAudioPump = audioPump
      const pendingAudioContext = audioContext
      const pendingAudioAbortController = audioPreparationAbortController
      const pendingAudioAbortCleanup = audioPreparationAbortCleanup
      stopped = true
      pendingAudioAbortController?.abort()
      pendingAudioAbortCleanup?.()
      if (encoder && encoder.state !== 'closed') {
        encoder.close()
      }
      cleanupPromise ??= output && !finalized ? output.cancel() : Promise.resolve()
      pendingPacketWrites.clear()
      encoder = null
      source = null
      output = null
      audioSource = null
      audioPump = null
      audioContext = null
      audioPreparation = null
      audioPreparationAbortController = null
      audioPreparationAbortCleanup = null
      try {
        await cleanupPromise
      } finally {
        if (!finalized) {
          await warnOnCleanupFailure(
            encoderOutput.abort(),
            'Video export encoder output abort failed.'
          )
        }
        await settleAudioPump(pendingAudioPump)
        await closeAudioContext(pendingAudioContext)
        audioPumpError = null
        cleanupPromise = null
      }
    },
  }
}

export function createBrowserVideoMp4FrameEncoder(
  settings: Partial<BrowserVideoMp4ExportSettings> = {}
): BrowserVideoMp4FrameEncoder {
  const normalizedSettings = normalizeBrowserVideoMp4ExportSettings(settings)
  const core = createBrowserVideoMp4FrameEncoderCore(
    normalizedSettings,
    createBufferEncoderOutput()
  )
  return {
    mimeType: core.mimeType,
    extension: core.extension,
    start: (nextSettings, options) => core.start(nextSettings, options),
    addFrame: (frame, request, signal) => core.addFrame(frame, request, signal),
    finish: (signal) => core.finish(signal),
    cancel: () => core.cancel(),
    dispose: () => core.dispose(),
  }
}

export function createBrowserVideoMp4StreamFrameEncoder(
  settings: Required<BrowserVideoMp4ExportSettings>,
  sink: BrowserVideoMp4OutputSink,
  options: { chunkSize?: number } = {}
): BrowserVideoMp4StreamFrameEncoder {
  const core = createBrowserVideoMp4FrameEncoderCore(
    settings,
    createStreamEncoderOutput(sink, options.chunkSize)
  )
  return {
    mimeType: core.mimeType,
    extension: core.extension,
    start: (nextSettings, nextOptions) => core.start(nextSettings, nextOptions),
    addFrame: (frame, request, signal) => core.addFrame(frame, request, signal),
    finish: (signal) => core.finish(signal),
    cancel: () => core.cancel(),
    dispose: () => core.dispose(),
  }
}

function createBufferEncoderOutput(): BrowserVideoMp4EncoderOutput<Blob> {
  const target = new BufferTarget()
  return {
    target,
    formatOptions: { fastStart: 'in-memory' },
    finalize: () => {
      const buffer = target.buffer
      if (!buffer) {
        throw new Error('Video encoder did not produce an output buffer.')
      }
      return new Blob([buffer], { type: 'video/mp4' })
    },
    abort: async () => undefined,
  }
}

function createStreamEncoderOutput(
  sink: BrowserVideoMp4OutputSink,
  chunkSize = DEFAULT_BROWSER_VIDEO_MP4_STREAM_CHUNK_SIZE
): BrowserVideoMp4EncoderOutput<BrowserVideoMp4StreamEncoderFinishResult> {
  let byteLength = 0
  let aborted = false
  const writable = new WritableStream<StreamTargetChunk>({
    async write(chunk) {
      if (aborted) {
        throw new Error('Browser MP4 stream output was aborted.')
      }
      await sink.write(chunk.data, chunk.position)
      byteLength = Math.max(byteLength, chunk.position + chunk.data.byteLength)
    },
    async close() {
      await sink.close()
    },
    async abort(reason) {
      aborted = true
      await sink.abort?.(reason)
    },
  })
  const target = new StreamTarget(writable, { chunked: true, chunkSize })
  return {
    target,
    formatOptions: { fastStart: 'fragmented' },
    finalize: () => ({ byteLength }),
    abort: async (reason) => {
      if (aborted) return
      aborted = true
      await sink.abort?.(reason)
    },
  }
}

const DEFAULT_BROWSER_VIDEO_MP4_STREAM_CHUNK_SIZE = 8 * 1024 * 1024

type BrowserVideoMp4ProgressReporter = (
  stage: BrowserVideoMp4ExportStage,
  frameNumber: number,
  message: string,
  diagnostics?: VideoRenderDiagnostics,
  phase?: BrowserVideoMp4ExportPhase,
  elapsedMs?: number,
  byteLength?: number,
  phaseStatus?: BrowserVideoMp4ExportPhaseEvent['status']
) => void

interface BrowserVideoMp4ExportTransformContext {
  diagnosticId: string
  totalFrames: number
  framesEncoded: number
  runtime: BrowserVideoRenderRuntime | null
  progress: BrowserVideoMp4ProgressReporter
  reportPhase: (event: BrowserVideoMp4ExportPhaseEvent) => void
}

interface BrowserVideoMp4ExportTransformResult<FinalResult> {
  result: FinalResult
  byteLength: number
  completionPhase?: BrowserVideoMp4ExportPhase
  completionElapsedMs?: number
}

interface BrowserVideoMp4ExportRunOptions<EncodedResult, FinalResult> {
  settings: Required<BrowserVideoMp4ExportSettings>
  totalFrames: number
  createEncoder: () => BrowserVideoMp4FrameEncoderCore<EncodedResult>
  transformEncoded: (
    encoded: EncodedResult,
    context: BrowserVideoMp4ExportTransformContext
  ) => Promise<BrowserVideoMp4ExportTransformResult<FinalResult>>
}

interface BrowserVideoMp4ExportRunResult<FinalResult> {
  result: FinalResult
  byteLength: number
  framesEncoded: number
  diagnosticEvents: VideoRenderDiagnosticEvent[]
}

async function runBrowserVideoMp4Export<EncodedResult, FinalResult>(
  options: BrowserVideoMp4ExportOptions,
  run: BrowserVideoMp4ExportRunOptions<EncodedResult, FinalResult>
): Promise<BrowserVideoMp4ExportRunResult<FinalResult>> {
  const { settings, totalFrames } = run
  const exportStartedAt = getLoggerManager().now()
  const diagnosticId = options.diagnosticId ?? ''
  const diagnosticEvents: VideoRenderDiagnosticEvent[] = []
  let runtime: BrowserVideoRenderRuntime | null = options.runtime ?? null
  const ownsRuntime = !options.runtime
  let encoder: BrowserVideoMp4FrameEncoderCore<EncodedResult> | null = null
  let framesEncoded = 0
  let frameEncodeStartedAt = 0

  const progress: BrowserVideoMp4ProgressReporter = (
    stage,
    frameNumber,
    message,
    diagnostics,
    phase,
    elapsedMs,
    byteLength,
    phaseStatus
  ) => {
    options.onProgress?.({
      stage,
      frameNumber,
      totalFrames,
      progress: totalFrames <= 0 ? 0 : frameNumber / totalFrames,
      message,
      phase,
      elapsedMs,
      byteLength,
      phaseStatus,
      diagnostics,
    })
  }

  const reportPhase = (event: BrowserVideoMp4ExportPhaseEvent): void => {
    const backendDiagnostics = getVideoRenderBackendDiagnostics(
      runtime,
      options.runtimeConfig?.graphics?.backend ?? 'webgl2'
    )
    const reportedEvent = { ...event, ...backendDiagnostics }
    options.onPhase?.(reportedEvent)
    const stage: BrowserVideoMp4ExportStage = event.status === 'failed' ? 'error' : 'encode'
    progress(
      stage,
      event.frameNumber ?? framesEncoded,
      `Video export phase ${event.phase} ${event.status}.`,
      runtime?.diagnostics,
      event.phase,
      event.elapsedMs,
      undefined,
      event.status
    )
  }

  try {
    logVideoRenderPhase(
      'export.total',
      'start',
      diagnosticId,
      exportStartedAt,
      `frames=${totalFrames}`,
      reportPhase
    )
    encoder = run.createEncoder()
    throwIfAborted(options.signal)
    progress('runtime', 0, 'Preparing video runtime.')
    runtime ??= await runVideoRenderPhase(
      'runtime.init',
      diagnosticId,
      () =>
        createBrowserVideoRenderRuntime({
          container: options.container,
          width: settings.width,
          height: settings.height,
          pixelRatio: settings.pixelRatio,
          ...options.runtimeConfig,
          onDiagnosticEvent: (event) => {
            diagnosticEvents.push(event)
            options.runtimeConfig?.onDiagnosticEvent?.(event)
          },
          onProgress: (event) => {
            options.runtimeConfig?.onProgress?.(event)
          },
        }),
      reportPhase
    )
    const activeRuntime = runtime
    if (!activeRuntime) {
      throw new Error('Video render runtime initialization failed.')
    }

    progress('stabilize', 0, 'Waiting for render stability.', activeRuntime.diagnostics)
    await runVideoRenderPhase(
      'stabilize',
      diagnosticId,
      async () => {
        await activeRuntime.loadScene(options.scene)
        await activeRuntime.waitForGsplatRenderable(options.signal)
      },
      reportPhase
    )

    if (options.timelineTemplate !== undefined) {
      progress('template', 0, 'Applying timeline template.', activeRuntime.diagnostics)
      await runVideoRenderPhase(
        'template',
        diagnosticId,
        () => activeRuntime.applyTimelineTemplate(options.timelineTemplate),
        reportPhase
      )
    }

    progress('encode', 0, 'Starting browser encoder.', activeRuntime.diagnostics)
    await encoder.start(settings, {
      audioUrl: options.audioUrl,
      signal: options.signal,
      diagnosticId,
      onPhase: reportPhase,
    })

    const clock = new ManualFrameClock(settings)
    frameEncodeStartedAt = getLoggerManager().now()
    logVideoRenderPhase(
      'frame-encode',
      'start',
      diagnosticId,
      frameEncodeStartedAt,
      '',
      reportPhase
    )
    for (let frameNumber = 0; frameNumber < totalFrames; frameNumber += 1) {
      throwIfAborted(options.signal)
      const request = clock.frameAt(frameNumber)
      progress(
        'render',
        frameNumber,
        `Rendering frame ${frameNumber + 1}/${totalFrames}.`,
        activeRuntime.diagnostics
      )
      await runVideoRenderPhase(
        'frame-render',
        diagnosticId,
        () => activeRuntime.renderFrame(request),
        reportPhase,
        frameNumber
      )
      throwIfAborted(options.signal)
      const frame = await runVideoRenderPhase(
        'frame-readback',
        diagnosticId,
        () => activeRuntime.readFrame(),
        reportPhase,
        frameNumber
      )
      throwIfAborted(options.signal)
      await encoder.addFrame(frame, request, options.signal)
      framesEncoded += 1
      progress(
        'encode',
        frameNumber + 1,
        `Encoded frame ${frameNumber + 1}/${totalFrames}.`,
        activeRuntime.diagnostics
      )
      await yieldToBrowser(frameNumber)
    }

    throwIfAborted(options.signal)
    logVideoRenderPhase(
      'frame-encode',
      'complete',
      diagnosticId,
      frameEncodeStartedAt,
      `frames=${framesEncoded}`,
      reportPhase
    )
    progress(
      'encode',
      totalFrames,
      'Finalizing encoded video.',
      activeRuntime.diagnostics,
      'frame-encode',
      getLoggerManager().now() - frameEncodeStartedAt,
      undefined,
      'complete'
    )
    const encoded = await encoder.finish(options.signal)
    const transformed = await run.transformEncoded(encoded, {
      diagnosticId,
      totalFrames,
      framesEncoded,
      runtime: activeRuntime,
      progress,
      reportPhase,
    })
    progress(
      'complete',
      totalFrames,
      'Video export complete.',
      activeRuntime.diagnostics,
      transformed.completionPhase,
      transformed.completionElapsedMs,
      transformed.byteLength,
      'complete'
    )
    logVideoRenderPhase(
      'export.total',
      'complete',
      diagnosticId,
      exportStartedAt,
      `frames=${framesEncoded} bytes=${transformed.byteLength}`,
      reportPhase
    )
    return {
      result: transformed.result,
      byteLength: transformed.byteLength,
      framesEncoded,
      diagnosticEvents,
    }
  } catch (error) {
    logVideoRenderPhase(
      'export.total',
      'failed',
      diagnosticId,
      exportStartedAt,
      `errorName=${getErrorName(error)}`,
      reportPhase
    )
    if (isDeviceLostError(error)) {
      logVideoRenderPhase(
        'device-lost',
        'failed',
        diagnosticId,
        exportStartedAt,
        `errorName=${getErrorName(error)}`,
        reportPhase
      )
    }
    if (isAbortError(error)) {
      progress('cancelled', framesEncoded, 'Video export cancelled.', runtime?.diagnostics)
    } else {
      progress('error', framesEncoded, getErrorMessage(error), runtime?.diagnostics)
    }
    await warnOnCleanupFailure(encoder?.cancel(), 'Video export encoder cancel failed.')
    throw error
  } finally {
    if (ownsRuntime) {
      await warnOnCleanupFailure(runtime?.dispose(), 'Video export runtime cleanup failed.')
    }
    await warnOnCleanupFailure(encoder?.dispose(), 'Video export encoder cleanup failed.')
  }
}

export async function exportBrowserVideoMp4(
  options: BrowserVideoMp4ExportOptions
): Promise<BrowserVideoMp4ExportResult> {
  const settings = applyGraphicsOutputLimits(
    normalizeBrowserVideoMp4ExportSettings(options.settings),
    options.runtimeConfig?.graphics
  )
  const totalFrames = Math.max(1, Math.ceil(settings.durationSeconds * settings.fps))
  const runResult = await runBrowserVideoMp4Export<
    Blob,
    Omit<BrowserVideoMp4ExportResult, 'diagnosticEvents'>
  >(options, {
    settings,
    totalFrames,
    createEncoder: () => options.encoder ?? createBrowserVideoMp4FrameEncoder(settings),
    transformEncoded: async (blob, context) => {
      const arrayBufferStartedAt = getLoggerManager().now()
      logVideoRenderPhase(
        'blob.array-buffer',
        'start',
        context.diagnosticId,
        arrayBufferStartedAt,
        '',
        context.reportPhase
      )
      const arrayBuffer = await blob.arrayBuffer()
      const arrayBufferElapsedMs = getLoggerManager().now() - arrayBufferStartedAt
      logVideoRenderPhase(
        'blob.array-buffer',
        'complete',
        context.diagnosticId,
        arrayBufferStartedAt,
        `bytes=${arrayBuffer.byteLength}`,
        context.reportPhase
      )
      return {
        result: {
          blob,
          arrayBuffer,
          mimeType: 'video/mp4',
          extension: 'mp4',
          width: settings.width,
          height: settings.height,
          fps: settings.fps,
          durationSeconds: settings.durationSeconds,
          framesEncoded: context.framesEncoded,
          byteLength: arrayBuffer.byteLength,
        },
        byteLength: arrayBuffer.byteLength,
        completionPhase: 'blob.array-buffer',
        completionElapsedMs: arrayBufferElapsedMs,
      }
    },
  })
  return {
    ...runResult.result,
    diagnosticEvents: runResult.diagnosticEvents,
  }
}

export async function exportBrowserVideoMp4ToSink(
  options: BrowserVideoMp4StreamExportOptions
): Promise<BrowserVideoMp4StreamExportResult> {
  const settings = applyGraphicsOutputLimits(
    normalizeBrowserVideoMp4StreamExportSettings(options.settings, {
      maxDurationSeconds: options.maxDurationSeconds,
      maxOutputFrames: options.maxOutputFrames,
    }),
    options.runtimeConfig?.graphics
  )
  const totalFrames = Math.max(1, Math.ceil(settings.durationSeconds * settings.fps))
  const exportOptions: BrowserVideoMp4ExportOptions = {
    container: options.container,
    settings,
    scene: options.scene,
    timelineTemplate: options.timelineTemplate,
    runtimeConfig: options.runtimeConfig,
    runtime: options.runtime,
    audioUrl: options.audioUrl,
    diagnosticId: options.diagnosticId,
    onPhase: options.onPhase,
    signal: options.signal,
    onProgress: options.onProgress,
  }
  const runResult = await runBrowserVideoMp4Export<
    BrowserVideoMp4StreamEncoderFinishResult,
    Omit<BrowserVideoMp4StreamExportResult, 'diagnosticEvents'>
  >(exportOptions, {
    settings,
    totalFrames,
    createEncoder: () =>
      options.encoder ??
      createBrowserVideoMp4StreamFrameEncoder(settings, options.sink, {
        chunkSize: options.chunkSize,
      }),
    transformEncoded: async (encoded, context) => ({
      result: {
        mimeType: 'video/mp4',
        extension: 'mp4',
        width: settings.width,
        height: settings.height,
        fps: settings.fps,
        durationSeconds: settings.durationSeconds,
        framesEncoded: context.framesEncoded,
        byteLength: encoded.byteLength,
        fragmented: true,
      },
      byteLength: encoded.byteLength,
    }),
  })
  return {
    ...runResult.result,
    diagnosticEvents: runResult.diagnosticEvents,
  }
}

export function createMissingVideoEncoderMessage(): string {
  const protocol = typeof window === 'undefined' ? 'unknown' : window.location.protocol
  const host = typeof window === 'undefined' ? 'unknown' : window.location.host
  const secureContext = typeof window === 'undefined' ? false : window.isSecureContext
  const userAgent = typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent
  const probableCause = secureContext
    ? 'The current browser/WebView does not expose WebCodecs VideoEncoder.'
    : 'WebCodecs VideoEncoder requires a secure context. Use HTTPS or localhost instead of an insecure LAN/IP HTTP address.'

  return [
    'Current browser does not support MP4 video export because WebCodecs VideoEncoder is unavailable.',
    probableCause,
    `protocol=${protocol}, host=${host}, secureContext=${secureContext}, userAgent=${userAgent}`,
  ].join(' ')
}

function createH264CodecString(height: number): string {
  return height < 1080 ? 'avc1.420028' : 'avc1.640033'
}

function estimateVideoBitsPerSecond(settings: Required<BrowserVideoMp4ExportSettings>): number {
  const pixelsPerSecond = settings.width * settings.height * settings.fps
  return Math.round(pixelsPerSecond * (0.08 + settings.quality * 0.22))
}

function clampInteger(value: unknown, min: number, max: number, fallback: number): number {
  return Math.round(clampNumber(value, min, max, fallback))
}

function clampEvenInteger(value: unknown, min: number, max: number, fallback: number): number {
  const integer = clampInteger(value, min, max, fallback)
  return integer % 2 === 0 ? integer : Math.max(min, integer - 1)
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback
}

function applyGraphicsOutputLimits(
  settings: Required<BrowserVideoMp4ExportSettings>,
  graphics: GraphicsConfig | undefined
): Required<BrowserVideoMp4ExportSettings> {
  const maxPixelRatio = resolveGraphicsConfig(graphics).maxPixelRatio
  if (settings.pixelRatio <= maxPixelRatio) {
    return settings
  }
  return { ...settings, pixelRatio: maxPixelRatio }
}

function positiveFiniteNumber(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback
}

function positiveIntegerNumber(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : fallback
}

function assertNotStopped(stopped: boolean): void {
  if (stopped) {
    throw new DOMException('Video encoder has been stopped.', 'AbortError')
  }
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException('Video export was cancelled.', 'AbortError')
  }
}

type VideoRenderPhaseStatus = 'start' | 'complete' | 'failed'

function getVideoRenderBackendDiagnostics(
  runtime: BrowserVideoRenderRuntime | null,
  requestedFallback?: GraphicsBackendKind
): Pick<BrowserVideoMp4ExportPhaseEvent, 'requestedBackend' | 'actualBackend'> {
  const requestedBackend = runtime?.diagnostics.requestedBackend ?? requestedFallback
  const actualBackend = runtime?.diagnostics.actualBackend
  return {
    ...(requestedBackend ? { requestedBackend } : {}),
    ...(actualBackend ? { actualBackend } : {}),
  }
}

function logVideoRenderPhase(
  phase: BrowserVideoMp4ExportPhase,
  status: VideoRenderPhaseStatus,
  diagnosticId: string,
  startedAt: number,
  details = '',
  reporter?: (event: BrowserVideoMp4ExportPhaseEvent) => void,
  frameNumber?: number
): void {
  const trace = diagnosticId ? ` requestId=${diagnosticId}` : ''
  const elapsed = status === 'start' ? 0 : Math.max(0, getLoggerManager().now() - startedAt)
  const suffix = details ? ` ${details}` : ''
  log.info(
    `[videoRender] phase=${phase} status=${status}${trace} elapsedMs=${elapsed.toFixed(1)}${suffix}`
  )
  reporter?.({ phase, status, elapsedMs: elapsed, frameNumber })
}

async function runVideoRenderPhase<T>(
  phase: BrowserVideoMp4ExportPhase,
  diagnosticId: string,
  operation: () => Promise<T>,
  reporter?: (event: BrowserVideoMp4ExportPhaseEvent) => void,
  frameNumber?: number
): Promise<T> {
  const startedAt = getLoggerManager().now()
  logVideoRenderPhase(phase, 'start', diagnosticId, startedAt, '', reporter, frameNumber)
  try {
    const result = await operation()
    logVideoRenderPhase(phase, 'complete', diagnosticId, startedAt, '', reporter, frameNumber)
    return result
  } catch (error) {
    logVideoRenderPhase(
      phase,
      'failed',
      diagnosticId,
      startedAt,
      `errorName=${getErrorName(error)}`,
      reporter,
      frameNumber
    )
    throw error
  }
}

function isDeviceLostError(error: unknown): boolean {
  const message = `${getErrorName(error)} ${getErrorMessage(error)}`
  const normalized = message.toLowerCase().replace(/[\s_-]/g, '')
  return (
    normalized.includes('devicelost') ||
    normalized.includes('contextlost') ||
    normalized.includes('gpureset')
  )
}

function withAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  throwIfAborted(signal)
  if (!signal) {
    return promise
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      cleanup()
      reject(new DOMException('Video export was cancelled.', 'AbortError'))
    }
    const cleanup = () => {
      signal.removeEventListener('abort', onAbort)
    }
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      (value) => {
        cleanup()
        resolve(value)
      },
      (error) => {
        cleanup()
        reject(error)
      }
    )
  })
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return withAbort(new Promise((resolve) => window.setTimeout(resolve, ms)), signal)
}

async function yieldToBrowser(frameNumber: number): Promise<void> {
  if (frameNumber % 4 !== 0) {
    return
  }
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0))
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function getErrorName(error: unknown): string {
  return error instanceof Error ? error.name : typeof error
}

async function settleAudioPump(pump: Promise<void> | null): Promise<void> {
  if (!pump) return
  try {
    await pump
  } catch {
    // Cancellation may reject an in-flight audio add after the muxer closes.
    // The export caller has already entered cleanup, so this rejection is consumed here.
  }
}

async function warnOnCleanupFailure(
  promise: Promise<unknown> | undefined,
  message: string
): Promise<void> {
  try {
    await promise
  } catch (error) {
    log.warn(`${message}: ${getErrorMessage(error)}`)
  }
}
